import React from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { DetailContent, DetailNavigation, ItemCount, Video } from '@pkg/components';
import { useAppSelector } from '@pkg/hooks';

import { type ArtifactType } from '@app/client/common/structs/nft/artifacts';
import { getTranslationByArtifactKey, useQuery } from '@app/helpers';
import { PathParams } from '@app/routes/types';
import { artifactDisplayMetadata, usersOwnedArtifacts } from '@app/store/selectors/artifacts/artifacts-metadata';
import { artifactUrlToKeyMap } from '@app/store/types/artifacts';

import './ArtifactDetail.scss';

interface ArtifactDetailProps {
    artifactType: ArtifactType;
}
const contentFramerTransition = { type: 'spring', damping: 60, stiffness: 200, mass: 3, restDelta: 0.00001 };
const titleVariants = {
    hidden: { transition: contentFramerTransition, opacity: 0 },
    show: {
        opacity: 1,
        transition: contentFramerTransition,
    },
};
const contentVariants = {
    hidden: { transition: { duration: 0 }, opacity: 0 },
    show: {
        opacity: 1,
        transition: { ...contentFramerTransition, delay: 0.35 },
    },
};

export const ArtifactDetailMobile = ({ artifactType }: ArtifactDetailProps) => {
    const query = useQuery();
    const { t } = useTranslation(['artifacts']);
    const { [PathParams.ArtifactType]: currentArtifactType } = useParams();
    const artifactTypeDisplayItems = useAppSelector(artifactDisplayMetadata);
    const userArtifactOwnedItems = useAppSelector(usersOwnedArtifacts);
    const totalInCollection = userArtifactOwnedItems[artifactType]?.length;

    const expanded =
        !isEmpty(query.get('expanded')) &&
        artifactUrlToKeyMap.get(currentArtifactType as ArtifactType) === artifactType; // how to clean this up?

    const title = getTranslationByArtifactKey(t, artifactType);
    const displayItem = artifactTypeDisplayItems[artifactType];

    return (
        <div className={`yat-artifact-detail ${expanded ? 'yat-artifact-detail--active' : ''}`}>
            {displayItem && (
                <Video
                    artifactType={artifactType}
                    active={expanded}
                    muted={true}
                    videoUrl={displayItem.animation_url}
                    posterUrl={displayItem.thumbnail_image}
                />
            )}

            <AnimatePresence>
                {expanded && (
                    <motion.div
                        className="yat-artifact-detail__content"
                        initial="hidden"
                        animate="show"
                        exit="hidden"
                        variants={contentVariants}
                        key="content"
                    >
                        <DetailNavigation artifactType={artifactType} />
                        <DetailContent artifactType={artifactType} />
                    </motion.div>
                )}

                {!expanded && (
                    <motion.div
                        className="yat-artifact-detail__title"
                        initial="hidden"
                        animate="show"
                        exit="hidden"
                        variants={titleVariants}
                        key="title"
                    >
                        <span>{title}</span>
                        {totalInCollection && <ItemCount count={totalInCollection} />}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
